/** Day / night — shared storage key with owlex.xyz (`owlex-theme`). */

const STORAGE_KEY = "owlex-theme";

/** Night from 19:00 through 06:59 on the visitor's device clock. */
export function themeFromLocalTime(date = new Date()) {
  const hour = date.getHours();
  return hour >= 19 || hour < 7 ? "night" : "day";
}

function readStoredPreference() {
  try {
    return localStorage.getItem(STORAGE_KEY);
  } catch {
    return null;
  }
}

/** @param {string | null} stored */
export function isExplicitThemePreference(stored) {
  return stored === "night" || stored === "day";
}

/** @param {string | null} stored */
export function resolveThemeMode(stored) {
  if (stored === "night" || stored === "day") return stored;
  return themeFromLocalTime();
}

/** @param {Date} [from] */
function msUntilNextTimeBoundary(from = new Date()) {
  const hour = from.getHours();
  const next = new Date(from);
  if (hour >= 19 || hour < 7) {
    if (hour >= 19) next.setDate(next.getDate() + 1);
    next.setHours(7, 0, 0, 0);
  } else {
    next.setHours(19, 0, 0, 0);
  }
  return Math.max(1000, next.getTime() - from.getTime() + 500);
}

/** Poll local clock every 30 minutes (auto mode only). */
const AUTO_THEME_POLL_MS = 30 * 60 * 1000;

let appKitModal = null;
/** @type {ReturnType<typeof setTimeout> | null} */
let autoThemeTimer = null;
/** @type {ReturnType<typeof setInterval> | null} */
let autoThemeInterval = null;

export function registerAppKitModal(modal) {
  appKitModal = modal;
  syncAppKitTheme();
}

function syncAppKitTheme() {
  if (!appKitModal) return;
  const isNight = document.documentElement.getAttribute("data-theme") === "night";
  appKitModal.setThemeMode(isNight ? "dark" : "light");
}

export function initThemeLamp() {
  const html = document.documentElement;
  const lamp = document.getElementById("lamp-pull");
  const metaTheme = document.querySelector('meta[name="theme-color"]');
  const prefersReducedMotion = window.matchMedia(
    "(prefers-reduced-motion: reduce)"
  ).matches;

  /** @param {"night" | "day"} mode @param {{ persist?: boolean }} [opts] */
  function applyTheme(mode, opts = {}) {
    const { persist = false } = opts;
    if (mode === "night") {
      html.setAttribute("data-theme", "night");
    } else {
      html.removeAttribute("data-theme");
    }
    if (persist) {
      try {
        localStorage.setItem(STORAGE_KEY, mode);
      } catch (e) {
        void e;
      }
    }
    if (lamp) {
      lamp.setAttribute("aria-pressed", mode === "night" ? "true" : "false");
      lamp.setAttribute(
        "aria-label",
        mode === "night" ? "Switch to day mode" : "Switch to night mode"
      );
    }
    if (metaTheme) {
      metaTheme.setAttribute(
        "content",
        mode === "night" ? "#0c0f14" : "#f4f6f9"
      );
    }
    syncAppKitTheme();
    scheduleAutoThemeCheck();
  }

  function applyResolvedTheme() {
    applyTheme(resolveThemeMode(readStoredPreference()));
  }

  function clearAutoThemeTimer() {
    if (autoThemeTimer != null) {
      clearTimeout(autoThemeTimer);
      autoThemeTimer = null;
    }
  }

  function clearAutoThemeInterval() {
    if (autoThemeInterval != null) {
      clearInterval(autoThemeInterval);
      autoThemeInterval = null;
    }
  }

  function scheduleAutoThemeCheck() {
    clearAutoThemeTimer();
    clearAutoThemeInterval();
    if (isExplicitThemePreference(readStoredPreference())) return;
    autoThemeTimer = setTimeout(() => {
      applyResolvedTheme();
    }, msUntilNextTimeBoundary());
    autoThemeInterval = setInterval(() => {
      if (isExplicitThemePreference(readStoredPreference())) {
        clearAutoThemeInterval();
        return;
      }
      applyResolvedTheme();
    }, AUTO_THEME_POLL_MS);
  }

  applyResolvedTheme();

  document.addEventListener("visibilitychange", () => {
    if (document.visibilityState !== "visible") return;
    if (isExplicitThemePreference(readStoredPreference())) return;
    applyResolvedTheme();
  });

  lamp?.addEventListener("click", () => {
    const isNight = html.getAttribute("data-theme") === "night";
    const next = isNight ? "day" : "night";
    if (!prefersReducedMotion) {
      lamp.classList.add("lamp-pull--pulling");
      window.setTimeout(() => {
        lamp.classList.remove("lamp-pull--pulling");
      }, 480);
    }
    applyTheme(next, { persist: true });
  });
}
